import React, { useEffect, useRef, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tabs,
  List,
  Avatar,
  Tag,
  Space,
  Modal,
  message,
  Badge,
  Typography,
  Spin,
  Upload,
  Input,
  Select
} from 'antd'
import {
  LogoutOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  AuditOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import { formatPeriodLabel, getVehicleDueDate, hasExplicitChargeRange, isLeaseStarted, getAdjustmentRevenueDelta, getOutstanding as getOutstandingUtil, isReceivableVehicleStatus } from './vehicleManagerUtils'

const { Text, Title } = Typography

const CANVAS_HEIGHT = 180
type DrawPoint = { x: number; y: number }

const paidStatuses = ['cashier_done', 'accounted', 'completed']
const paymentMethodOptions = [
  '兴业银行',
  '微信',
  '公户',
  '信用社',
  '建设银行',
  '农业银行',
  '支付宝',
  '林乙乐',
  '689微信'
]

const accountingScenarioLabelMap: Record<string, string> = {
  auto: '自动识别（推荐）',
  rent_only: '情况1：仅收租金',
  rent_deposit: '情况2：租金+押金',
  rent_deposit_tradein: '情况3：租金+押金+旧车折抵',
  rent_tradein: '情况4：租金+旧车折抵',
  deposit_offset: '情况5：押金抵扣租金',
  deposit_only: '仅押金'
}
const getAccountingScenarioLabel = (value?: string) =>
  accountingScenarioLabelMap[String(value || 'auto')] || '自动识别（推荐）'

const getReminderMonths = (vehicle: any) => {
  const period = String(vehicle?.reminderPeriod || vehicle?.rentPeriod || 'monthly')
  const customMonths = Number(vehicle?.reminderPeriodMonths || vehicle?.rentPeriodMonths || 0)
  switch (period) {
    case 'quarterly':
      return 3
    case 'halfyear':
      return 6
    case 'yearly':
      return 12
    case 'custom':
      return customMonths > 0 ? customMonths : 1
    default:
      return 1
  }
}

const CashierPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [verifiedSearchKeyword, setVerifiedSearchKeyword] = useState('')
  const [loading, setLoading] = useState(true)
  const [pendingReceipts, setPendingReceipts] = useState<any[]>([])
  const [verifiedReceipts, setVerifiedReceipts] = useState<any[]>([])
  const [pendingConfirmReceipts, setPendingConfirmReceipts] = useState<any[]>([])
  const [statistics, setStatistics] = useState({
    pendingVerification: 0,
    verifiedToday: 0,
    pendingAmount: 0,
    totalProcessed: 0
  })
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [submittingConfirm, setSubmittingConfirm] = useState(false)
  const [currentReceipt, setCurrentReceipt] = useState<any>(null)
  const [receiptPreviewVisible, setReceiptPreviewVisible] = useState(false)
  const [receiptPreview, setReceiptPreview] = useState<any>(null)
  const [receiptPreviewMode, setReceiptPreviewMode] = useState<'pending' | 'verified'>('pending')
  const [receiptSignatureUrl, setReceiptSignatureUrl] = useState('')
  const [receiptMakerSealUrl, setReceiptMakerSealUrl] = useState('')
  const [cashierNotes, setCashierNotes] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<string>(paymentMethodOptions[0])
  const [leaseForms, setLeaseForms] = useState<any[]>([])
  const [signedLeaseForms, setSignedLeaseForms] = useState<any[]>([])
  const [leasePreviewVisible, setLeasePreviewVisible] = useState(false)
  const [leasePreview, setLeasePreview] = useState<any>(null)
  const [leaseConfirmVisible, setLeaseConfirmVisible] = useState(false)
  const [currentLeaseForm, setCurrentLeaseForm] = useState<any>(null)
  const [leaseSignatureUrl, setLeaseSignatureUrl] = useState('')
  const [leaseMakerSealUrl, setLeaseMakerSealUrl] = useState('')
  const [leaseSignatureList, setLeaseSignatureList] = useState<any[]>([])
  const [leaseSignatureDataUrl, setLeaseSignatureDataUrl] = useState<string>('')
  const [leaseNotes, setLeaseNotes] = useState('')
  const leaseCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const leaseDrawingRef = useRef(false)
  const leaseLastPointRef = useRef<DrawPoint | null>(null)
  const receiptPrintRef = useRef<HTMLDivElement | null>(null)
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)

  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  useEffect(() => {
    loadData()
  }, [])

  const normalizeReceipts = (list: any[]) =>
    list.map(r => {
      const items = getReceiptItems(r)
      const total = items.reduce((s: number, it: any) => s + (Number(it.expectedAmount ?? it.amount ?? 0) || 0), 0)
      const received = items.reduce((s: number, it: any) => s + (Number(it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0) || 0), 0)
      const first = items[0] || {}
      const firstExplicitRange = items.find((it: any) => hasExplicitChargeRange(it, r))
      const chargeRangeSource = firstExplicitRange || (hasExplicitChargeRange(undefined, r) ? r : null)
      return {
        ...r,
        receiptId: r._id || r.id,
        receiptStatus: r.status,
        amount: total || Number(r.amount || 0) || 0,
        verifiedAmount: received || Number(r.verifiedAmount || 0) || 0,
        vehicleId: r.vehicleId || first.vehicleId,
        plate: r.plate || first.plate || r.company || '合并收款',
        model: r.model || first.model,
        company: r.company || first.company,
        contactPerson: r.contactPerson || first.contactPerson,
        dueDate: r.dueDate || first.dueDate,
        chargeRangeStart: chargeRangeSource ? (chargeRangeSource.chargeRangeStart || r.chargeRangeStart) : undefined,
        chargeRangeEnd: chargeRangeSource ? (chargeRangeSource.chargeRangeEnd || r.chargeRangeEnd) : undefined,
        periodStart: r.periodStart || first.periodStart,
        periodEnd: r.periodEnd || first.periodEnd,
        rentPeriod: r.rentPeriod || first.rentPeriod,
        rawReceipt: r
      }
    })

  const getRangeLabel = (item: any) => {
    if (!hasExplicitChargeRange(item, item?.rawReceipt)) return ''
    const start = item.chargeRangeStart || item?.rawReceipt?.chargeRangeStart
    const end = item.chargeRangeEnd || item?.rawReceipt?.chargeRangeEnd
    if (!start || !end) return ''
    const startDay = dayjs(start)
    const endDay = dayjs(end)
    if (!startDay.isValid() || !endDay.isValid()) return ''
    return `${startDay.format('YYYY-MM-DD')} ~ ${endDay.format('YYYY-MM-DD')}`
  }

  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const getReceiptSignatureUrl = (receipt: any) => {
    const raw = receipt?.rawReceipt || receipt
    if (raw?.cashierSignatureUrl) return raw.cashierSignatureUrl
    const items = getReceiptItems(raw)
    const firstSigned = items.find((it: any) => it.cashierSignatureUrl)
    return firstSigned?.cashierSignatureUrl || ''
  }

  const resolveCloudFileUrl = async (rawUrl?: string) => {
    if (!rawUrl) return ''
    if (!String(rawUrl).startsWith('cloud://')) return rawUrl
    try {
      await ensureLogin()
      const storage = (app as any).storage?.()
      const useStorage = Boolean(storage?.getTempFileURL)
      const fileList = [{ fileID: rawUrl, maxAge: 24 * 60 * 60 }]
      const res: any = useStorage
        ? await storage.getTempFileURL({ fileList })
        : await (app as any).getTempFileURL({ fileList })
      return (
        res?.fileList?.[0]?.tempFileURL ||
        res?.fileList?.[0]?.tempFileUrl ||
        res?.fileList?.[0]?.download_url ||
        ''
      )
    } catch (err) {
      console.error(err)
      return ''
    }
  }

  const resolveReceiptSignatureUrl = async (receipt: any) => {
    const rawUrl = getReceiptSignatureUrl(receipt)
    const tempUrl = await resolveCloudFileUrl(rawUrl)
    setReceiptSignatureUrl(tempUrl || '')
  }

  const resolveReceiptMakerSealUrl = async (receipt: any) => {
    const raw = receipt?.rawReceipt || receipt
    let rawUrl = raw?.makerSealUrl || ''
    if (!rawUrl) {
      const identities = [raw?.makerId, raw?.createdBy]
        .map(v => String(v || '').trim())
        .filter(Boolean)
      for (const identity of identities) {
        const userRes = await simpleDirectCloudApi.getUserByIdentity(identity)
        const seal = userRes.success ? userRes.data?.makerSealUrl : ''
        if (seal) {
          rawUrl = seal
          break
        }
      }
    }
    let tempUrl = await resolveCloudFileUrl(rawUrl)
    setReceiptMakerSealUrl(tempUrl || '')
  }

  const openReceiptPreview = (item: any, mode: 'pending' | 'verified') => {
    setReceiptPreview(item)
    setReceiptPreviewVisible(true)
    setReceiptPreviewMode(mode)
  }

  const resolveLeaseSignatureUrl = async (lease: any) => {
    const rawUrl = lease?.cashierSignatureUrl || ''
    const tempUrl = await resolveCloudFileUrl(rawUrl)
    setLeaseSignatureUrl(tempUrl || '')
  }

  const resolveLeaseMakerSealUrl = async (lease: any) => {
    let rawUrl = lease?.makerSealUrl || ''
    if (!rawUrl) {
      const identities = [lease?.makerId, lease?.createdBy]
        .map(v => String(v || '').trim())
        .filter(Boolean)
      for (const identity of identities) {
        const userRes = await simpleDirectCloudApi.getUserByIdentity(identity)
        const seal = userRes.success ? userRes.data?.makerSealUrl : ''
        if (seal) {
          rawUrl = seal
          break
        }
      }
    }
    let tempUrl = await resolveCloudFileUrl(rawUrl)
    setLeaseMakerSealUrl(tempUrl || '')
  }

  useEffect(() => {
    if (!receiptPreviewVisible || !receiptPreview) return
    resolveReceiptSignatureUrl(receiptPreview)
    resolveReceiptMakerSealUrl(receiptPreview)
  }, [receiptPreviewVisible, receiptPreview])

  useEffect(() => {
    if (!leasePreviewVisible || !leasePreview) return
    resolveLeaseSignatureUrl(leasePreview)
    resolveLeaseMakerSealUrl(leasePreview)
  }, [leasePreviewVisible, leasePreview])

  const syncCanvasSize = (ref: React.RefObject<HTMLCanvasElement>) => {
    const canvas = ref.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.max(1, Math.round(rect.width * ratio))
    canvas.height = Math.max(1, Math.round(rect.height * ratio))
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.lineWidth = 3.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.imageSmoothingEnabled = true
    ctx.globalCompositeOperation = 'source-over'
  }

  useEffect(() => {
    if (!leaseConfirmVisible) return
    syncCanvasSize(leaseCanvasRef)
    const onResize = () => syncCanvasSize(leaseCanvasRef)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [leaseConfirmVisible])

  const formatDate = (value?: string) => {
    const d = value ? dayjs(value) : dayjs()
    return d.isValid() ? d.format('YYYY-MM-DD') : ''
  }

  const formatMoney = (value: any) => Number(value || 0).toFixed(2)
  const getActualIncomeAmount = (item: any) =>
    Number(
      item?.actualAmount ??
      item?.actualAmountDraft ??
      item?.verifiedAmount ??
      item?.receivedAmount ??
      item?.amount ??
      item?.rawReceipt?.actualAmountDraft ??
      item?.rawReceipt?.actualAmount ??
      0
    ) || 0
  const needsCashierConfirm = (item: any) =>
    String(item?.receiptStatus || item?.status || '') === 'submitted' && getActualIncomeAmount(item) > 0
  const isDepositOnlyReceipt = (item: any) =>
    String(item?.receiptType || item?.rawReceipt?.receiptType || '') === 'deposit_only'

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    const pendingIds = pendingReceipts.map(p => p.vehicleId || p.plate)
    return vehicles
      .filter(v => isReceivableVehicleStatus(v.status))
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => !pendingIds.includes(v.id) && !pendingIds.includes(v._id))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)
  const filteredVerifiedReceipts = (() => {
    const keyword = String(verifiedSearchKeyword || '').trim().toLowerCase()
    if (!keyword) return verifiedReceipts
    return verifiedReceipts.filter((item: any) => {
      const texts = [
        item?.plate,
        item?.company,
        item?.createdBy,
        item?.cashierName,
        item?.notes,
        item?.receiptId,
        item?._id,
        item?.id
      ].map(v => String(v || '').toLowerCase())
      return texts.some(t => t.includes(keyword))
    })
  })()
  const pendingReturnForms = leaseForms.filter((item: any) => item.type === 'return')
  const signedReturnVehicleIds = new Set(
    signedLeaseForms
      .filter((item: any) => item.type === 'return')
      .map((item: any) => String(item.vehicleId || item.plate || ''))
      .filter(Boolean)
  )
  const maintenanceWithoutSignedReturnDoc = vehicles.filter((v: any) => {
    if (v.status !== 'maintenance') return false
    const key = String(v._id || v.id || v.plate || '')
    const plate = String(v.plate || '')
    return !signedReturnVehicleIds.has(key) && !signedReturnVehicleIds.has(plate)
  })

  const handleExportReceipt = () => {
    if (!receiptPrintRef.current) return
    const content = receiptPrintRef.current.innerHTML
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`
      <html>
        <head>
          <title>receipt</title>
          <style>
            body { margin: 24px; font-family: "SimSun","STSong","Noto Serif SC",serif; color: #222; }
            img { max-width: 100%; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `)
    win.document.close()
    win.focus()
    win.print()
  }

  const mapWithVehicle = (list: any[]) =>
    list.map(r => {
      const v = vehicles.find(vh => vh.id === r.vehicleId || vh._id === r.vehicleId || vh.plate === r.plate)
      const reminderDue = v ? getVehicleDueDate(v) : r.dueDate
      return {
        ...r,
        plate: r.plate || v?.plate || '未知车牌',
        model: r.model || v?.type || '',
        company: r.company || v?.company || '',
        contactPerson: r.contactPerson || v?.contactPerson || '',
        amount: r.amount ?? v?.rentAmount ?? 0,
        dueDate: r.dueDate || v?.dueDate,
        reminderDue,
        reminderAmount: v?.reminderAmount ?? v?.rentAmount,
        reminderPeriod: v?.reminderPeriod || v?.rentPeriod,
        reminderPeriodMonths: v?.reminderPeriodMonths
      }
    })

  const loadData = async () => {
    try {
      setLoading(true)
      const [_, allRes, adjustmentsRes, leaseRes] = await Promise.all([
        fetchVehicles().catch(() => {}),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getLeaseForms()
      ])

      const allRaw = allRes.success && allRes.data ? allRes.data : []
      const latestVehicles = useVehicleStore.getState().vehicles || []
      const pendingRaw = allRaw.filter((r: any) => r.status === 'submitted')
      const verifiedRaw = allRaw.filter((r: any) => paidStatuses.includes(r.status))
      const adjustmentsRaw = adjustmentsRes.success && adjustmentsRes.data ? adjustmentsRes.data : []
      const adjustmentsClean = adjustmentsRaw.filter((adj: any) => adj.type === 'parts' || adj.type === 'repair')
      const leaseRaw = leaseRes.success && leaseRes.data ? leaseRes.data : []
      const pendingLease = leaseRaw.filter((l: any) => l.status !== 'signed')
      const signedLease = leaseRaw.filter((l: any) => l.status === 'signed')

      const mapWithVehicleFromList = (list: any[]) =>
        list.map(r => {
          const v = latestVehicles.find(vh => vh.id === r.vehicleId || vh._id === r.vehicleId || vh.plate === r.plate)
          const reminderDue = v ? getVehicleDueDate(v) : r.dueDate
          return {
            ...r,
            plate: r.plate || v?.plate || '未知车牌',
            model: r.model || v?.type || '',
            company: r.company || v?.company || '',
            contactPerson: r.contactPerson || v?.contactPerson || '',
            amount: r.amount ?? v?.rentAmount ?? 0,
            dueDate: r.dueDate || v?.dueDate,
            reminderDue,
            reminderAmount: v?.reminderAmount ?? v?.rentAmount,
            reminderPeriod: v?.reminderPeriod || v?.rentPeriod,
            reminderPeriodMonths: v?.reminderPeriodMonths
          }
        })

      const pending = mapWithVehicleFromList(normalizeReceipts(pendingRaw))
      const verified = mapWithVehicleFromList(normalizeReceipts(verifiedRaw))
      const pendingConfirm = pending.filter((item: any) => needsCashierConfirm(item))

      const today = new Date().toDateString()
      const verifiedToday = verified.filter(r => {
        const t = r.verifiedAt || r.accountedAt || r.updatedAt || r.createdAt
        if (!t) return false
        return new Date(t).toDateString() === today
      }).length
      const pendingAmount = (latestVehicles || [])
        .filter((v: any) => isReceivableVehicleStatus(v?.status))
        .reduce((sum: number, vehicle: any) => sum + ((vehicle as any)._outstanding ?? 0), 0)
      const adjustmentsDelta = getAdjustmentRevenueDelta(adjustmentsClean)
      const paidIncome = verifiedRaw.reduce((sum: number, receipt: any) => {
        const items = getReceiptItems(receipt)
        if (items.length) {
          return sum + items.reduce((inner: number, it: any) => inner + (Number(it.actualAmount ?? it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0) || 0), 0)
        }
        return sum + (Number(receipt.actualAmount ?? receipt.verifiedAmount ?? receipt.amount ?? 0) || 0)
      }, 0)
      const totalProcessed = paidIncome + adjustmentsDelta

      setPendingReceipts(pending)
      setPendingConfirmReceipts(pendingConfirm)
      setVerifiedReceipts(verified)
      setLeaseForms(pendingLease)
      setSignedLeaseForms(signedLease)
      setStatistics({
        pendingVerification: pending.length,
        verifiedToday,
        pendingAmount,
        totalProcessed
      })
    } catch (error) {
      console.error(error)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const handleConfirmReceipt = (receipt: any) => {
    const methodFromReceipt =
      receipt?.paymentMethod ||
      receipt?.rawReceipt?.paymentMethod ||
      receipt?.rawReceipt?.items?.[0]?.paymentMethod
    setCurrentReceipt(receipt)
    setCashierNotes('')
    setPaymentMethod(methodFromReceipt || paymentMethodOptions[0])
    setConfirmVisible(true)
  }

  const openLeasePreview = (item: any) => {
    setLeasePreview(item)
    setLeasePreviewVisible(true)
  }

  const handleConfirmLease = (item: any) => {
    setCurrentLeaseForm(item)
    setLeaseSignatureList([])
    setLeaseSignatureDataUrl('')
    setLeaseNotes('')
    clearLeasePad()
    setLeaseConfirmVisible(true)
  }

  const getPos = (
    e: React.MouseEvent<HTMLCanvasElement> |
      React.TouchEvent<HTMLCanvasElement> |
      React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement>
  ) => {
    const canvas = ref.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      const t = e.touches[0]
      return { x: t.clientX - rect.left, y: t.clientY - rect.top }
    }
    if ('clientX' in e && 'clientY' in e) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top }
  }

  const startStroke = (
    e: React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>
  ) => {
    e.preventDefault()
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    canvas.setPointerCapture?.(e.pointerId)
    const { x, y } = getPos(e, ref)
    ctx.strokeStyle = '#000'
    ctx.fillStyle = '#000'
    drawingRef.current = true
    lastPointRef.current = { x, y }
    // Draw a solid starting point so quick taps are not lost.
    ctx.beginPath()
    ctx.arc(x, y, Math.max(1.2, ctx.lineWidth / 2), 0, Math.PI * 2)
    ctx.fill()
  }

  const moveStroke = (
    e: React.PointerEvent<HTMLCanvasElement>,
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>
  ) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e, ref)
    const prev = lastPointRef.current
    if (!prev) {
      lastPointRef.current = { x, y }
      return
    }
    const dx = x - prev.x
    const dy = y - prev.y
    const distance = Math.hypot(dx, dy)
    if (distance < 0.001) return
    const steps = Math.max(1, Math.ceil(distance / 1.6))
    ctx.beginPath()
    ctx.moveTo(prev.x, prev.y)
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps
      const nx = prev.x + dx * t
      const ny = prev.y + dy * t
      ctx.lineTo(nx, ny)
    }
    ctx.stroke()
    lastPointRef.current = { x, y }
  }

  const endStroke = (
    ref: React.RefObject<HTMLCanvasElement>,
    drawingRef: React.MutableRefObject<boolean>,
    lastPointRef: React.MutableRefObject<DrawPoint | null>,
    setDataUrl: (value: string) => void
  ) => {
    if (!drawingRef.current) return
    drawingRef.current = false
    lastPointRef.current = null
    const canvas = ref.current
    if (canvas) {
      setDataUrl(canvas.toDataURL('image/png'))
    }
  }

  const startLeaseDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    startStroke(e, leaseCanvasRef, leaseDrawingRef, leaseLastPointRef)
  }

  const drawLease = (e: React.PointerEvent<HTMLCanvasElement>) => {
    moveStroke(e, leaseCanvasRef, leaseDrawingRef, leaseLastPointRef)
  }

  const endLeaseDraw = () => {
    endStroke(leaseCanvasRef, leaseDrawingRef, leaseLastPointRef, setLeaseSignatureDataUrl)
  }

  const clearLeasePad = () => {
    const canvas = leaseCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setLeaseSignatureDataUrl('')
    leaseDrawingRef.current = false
    leaseLastPointRef.current = null
  }

  const handleSubmitConfirm = async () => {
    if (submittingConfirm) return
    if (!currentReceipt) return
    try {
      setSubmittingConfirm(true)
      const [allReceiptsRes, adjustmentsRes] = await Promise.all([
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments()
      ])
      const beforeReceipts = allReceiptsRes.success && allReceiptsRes.data ? allReceiptsRes.data : []
      const adjustmentsRaw = adjustmentsRes.success && adjustmentsRes.data ? adjustmentsRes.data : []
      const adjustmentsClean = adjustmentsRaw.filter((adj: any) => adj.type === 'parts' || adj.type === 'repair')
      const receiptKey = currentReceipt.receiptId || currentReceipt._id || currentReceipt.id || currentReceipt.plate
      const autoSignatureCanvas = document.createElement('canvas')
      autoSignatureCanvas.width = 480
      autoSignatureCanvas.height = 160
      const autoCtx = autoSignatureCanvas.getContext('2d')
      if (autoCtx) {
        autoCtx.fillStyle = '#ffffff'
        autoCtx.fillRect(0, 0, autoSignatureCanvas.width, autoSignatureCanvas.height)
        autoCtx.fillStyle = '#111111'
        autoCtx.font = 'bold 38px KaiTi, SimSun, serif'
        autoCtx.fillText('出纳核验', 24, 66)
        autoCtx.font = '28px KaiTi, SimSun, serif'
        autoCtx.fillText(user?.nickName || user?.nickname || user?.username || 'cashier', 24, 114)
        autoCtx.font = '20px SimSun, serif'
        autoCtx.fillText(dayjs().format('YYYY-MM-DD HH:mm'), 24, 144)
      }
      const autoSignatureDataUrl = autoSignatureCanvas.toDataURL('image/png')
      const autoSignatureBlob = await (await fetch(autoSignatureDataUrl)).blob()
      const autoSignatureFile = new File([autoSignatureBlob], `signature-${receiptKey}-${Date.now()}.png`, { type: 'image/png' })
      const autoSignatureCloudPath = `receipts/signature-${receiptKey}-${Date.now()}.png`
      const autoSignatureUploadRes: any = await app.uploadFile({ cloudPath: autoSignatureCloudPath, filePath: autoSignatureFile } as any)
      const signatureUrl = autoSignatureUploadRes?.fileID || ''

      const raw = currentReceipt.rawReceipt || {}
      const items = Array.isArray(raw.items) && raw.items.length ? raw.items : [{ ...raw }]
      const verifiedAt = new Date().toISOString()
      const newItems = items.map((it: any) => ({
        ...it,
        actualAmount: Number(it.actualAmount ?? it.actualAmountDraft ?? it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0),
        receivedAmount: Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0),
        status: 'cashier_done',
        verifiedAt,
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        cashierNotes,
        cashierSignatureReceiptId: receiptKey,
        paymentMethod
      }))

      const normalizeKey = (value: any) => String(value ?? '').trim().toUpperCase()
      const matchVehicleByAnyId = (vehicleId: any, plate: any) =>
        vehicles.find((vehicle: any) => {
          const keys = [vehicle._id, vehicle.id, vehicle.plate, vehicle.forklift_no]
            .map(normalizeKey)
            .filter(Boolean)
          return keys.includes(normalizeKey(vehicleId)) || keys.includes(normalizeKey(plate))
        })

      const updatedReceipt = {
        ...(currentReceipt.rawReceipt || currentReceipt),
        status: 'cashier_done',
        actualAmount: Number(
          (currentReceipt.rawReceipt || currentReceipt)?.actualAmount ??
          (currentReceipt.rawReceipt || currentReceipt)?.actualAmountDraft ??
          newItems.reduce((sum: number, it: any) => sum + (Number(it.actualAmount ?? 0) || 0), 0)
        ) || 0,
        items: newItems,
        verifiedAt,
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: '',
        cashierNotes,
        paymentMethod
      }
      const afterReceipts = beforeReceipts.map((receipt: any) =>
        String(receipt._id || receipt.id) === String(currentReceipt.receiptId)
          ? { ...receipt, ...updatedReceipt }
          : receipt
      )

      const affectedVehicles = new Map<string, { vehicle: any; latestEnd: string; rentAmount: number }>()
      for (const item of newItems) {
        const vehicle = matchVehicleByAnyId(item?.vehicleId, item?.plate)
        if (!vehicle) continue
        const key = String(vehicle._id || vehicle.id || vehicle.plate)
        const endRaw = hasExplicitChargeRange(item) ? String(item?.chargeRangeEnd || '').trim() : ''
        const rentAmount = Number(item?.receivedAmount ?? item?.expectedAmount ?? item?.amount ?? 0) || 0
        const prev = affectedVehicles.get(key)
        const latestEnd = !prev?.latestEnd || (endRaw && dayjs(endRaw).isValid() && dayjs(endRaw).isAfter(dayjs(prev.latestEnd), 'day'))
          ? endRaw
          : (prev?.latestEnd || '')
        affectedVehicles.set(key, {
          vehicle,
          latestEnd,
          rentAmount: (prev?.rentAmount || 0) + rentAmount
        })
      }

      const anomalies: string[] = []
      for (const { vehicle, rentAmount } of affectedVehicles.values()) {
        if (rentAmount <= 0) continue
        const beforeOutstanding = getOutstandingUtil(vehicle, beforeReceipts, adjustmentsClean)
        const afterOutstanding = getOutstandingUtil(vehicle, afterReceipts, adjustmentsClean)
        if (beforeOutstanding > 1 && afterOutstanding >= beforeOutstanding - 1) {
          anomalies.push(`${vehicle.plate || vehicle.id}: 收前 ${beforeOutstanding.toFixed(2)}，收后 ${afterOutstanding.toFixed(2)}`)
        }
      }

      if (anomalies.length && !String(cashierNotes || '').trim()) {
        Modal.warning({
          title: '该收款无法直接确认',
          width: 720,
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>系统模拟后发现以下车辆收款后待收未下降。请先检查收款区间、起租日、代收算法；若确需放行，请在备注中填写原因后再确认。</div>
              {anomalies.map((text, idx) => (
                <div key={idx} style={{ marginBottom: 6 }}>{text}</div>
              ))}
            </div>
          )
        })
        return
      }

      const res = await simpleDirectCloudApi.updateReceipt(currentReceipt.receiptId, {
        status: 'cashier_done',
        items: newItems,
        verifiedAt,
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        cashierNotes,
        cashierSignatureReceiptId: receiptKey,
        paymentMethod,
        cashierOverrideReason: anomalies.length ? String(cashierNotes || '').trim() : undefined,
        cashierOverrideAt: anomalies.length ? verifiedAt : undefined
      })
      if (res.success) {
        for (const { vehicle, latestEnd } of affectedVehicles.values()) {
          if (!latestEnd || !dayjs(latestEnd).isValid()) continue
          const docId = vehicle._id || vehicle.id || vehicle.plate
          const nextReminderDate = dayjs(latestEnd).add(getReminderMonths(vehicle), 'month').format('YYYY-MM-DD')
          await simpleDirectCloudApi.updateVehicle(docId, {
            _id: docId,
            reminderDate: nextReminderDate
          } as any)
        }

        const anomalies: string[] = []
        for (const { vehicle, rentAmount } of affectedVehicles.values()) {
          if (rentAmount <= 0) continue
          const beforeOutstanding = getOutstandingUtil(vehicle, beforeReceipts, adjustmentsClean)
          const afterOutstanding = getOutstandingUtil(vehicle, afterReceipts, adjustmentsClean)
          if (beforeOutstanding > 1 && afterOutstanding >= beforeOutstanding - 1) {
            anomalies.push(`${vehicle.plate || vehicle.id}: 收款后待收未明显下降（收前 ${beforeOutstanding.toFixed(2)}，收后 ${afterOutstanding.toFixed(2)}）`)
          }
        }

        message.success(`已确认收款（${paymentMethod}）`)
        setConfirmVisible(false)
        setCurrentReceipt(null)
        setCashierNotes('')
        setPaymentMethod(paymentMethodOptions[0])
        await fetchVehicles().catch(() => {})
        loadData()
        if (anomalies.length) {
          message.warning('本次收款按备注原因放行，请后续复核')
        }
      } else {
        message.error(res.error || '确认失败')
      }
    } catch (error) {
      console.error(error)
      message.error('确认收款失败')
    } finally {
      setSubmittingConfirm(false)
    }
  }

  const handleSubmitLeaseConfirm = async () => {
    if (!currentLeaseForm) return
    if ((!leaseSignatureList.length || !leaseSignatureList[0].originFileObj) && !leaseSignatureDataUrl) {
      message.error('请上传签名')
      return
    }
    try {
      const leaseKey = currentLeaseForm._id || currentLeaseForm.id || currentLeaseForm.vehicleId
      let signatureUrl = ''
      if (leaseSignatureDataUrl) {
        const blob = await (await fetch(leaseSignatureDataUrl)).blob()
        const file = new File([blob], `lease-signature-${leaseKey}-${Date.now()}.png`, { type: 'image/png' })
        const cloudPath = `lease_forms/signature-${leaseKey}-${Date.now()}.png`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      } else if (leaseSignatureList.length && leaseSignatureList[0].originFileObj) {
        const file = leaseSignatureList[0].originFileObj
        const cloudPath = `lease_forms/signature-${leaseKey}-${Date.now()}-${file.name}`
        const uploadRes: any = await app.uploadFile({ cloudPath, filePath: file } as any)
        signatureUrl = uploadRes?.fileID || ''
      }

      const signedAt = new Date().toISOString()
      const res = await simpleDirectCloudApi.updateLeaseForm(currentLeaseForm._id || currentLeaseForm.id, {
        status: 'signed',
        cashierId: user?.id,
        cashierName: user?.nickName,
        cashierSignatureUrl: signatureUrl,
        signedAt,
        cashierNotes: leaseNotes
      })
      if (res.success) {
        if (currentLeaseForm.type === 'return' && currentLeaseForm.vehicleId) {
          await simpleDirectCloudApi.updateVehicle(currentLeaseForm.vehicleId, {
            _id: currentLeaseForm.vehicleId,
            status: 'maintenance',
            returnSigned: true
          } as any)
        }
        if (currentLeaseForm.type === 'relet' && currentLeaseForm.vehicleId) {
          await simpleDirectCloudApi.updateVehicle(currentLeaseForm.vehicleId, {
            _id: currentLeaseForm.vehicleId,
            status: 'rented',
            returnSigned: false,
            company: currentLeaseForm.unitName,
            startDate: currentLeaseForm.startDate,
            dueDate: currentLeaseForm.dueDate,
            rentAmount: Number(currentLeaseForm.rentAmount || 0) || undefined,
            rentPeriod: currentLeaseForm.rentPeriod,
            rentPeriodMonths: currentLeaseForm.rentPeriodMonths,
            reminderDate: '',
            reminderPeriod: currentLeaseForm.reminderPeriod,
            reminderPeriodMonths: currentLeaseForm.reminderPeriodMonths,
            reminderAmount: Number(currentLeaseForm.reminderAmount || 0) || undefined,
            receivableMode: currentLeaseForm.receivableMode,
            receivableMonths: currentLeaseForm.receivableMonths,
            address: currentLeaseForm.address,
            configuration: currentLeaseForm.configuration
          } as any)
        }
        message.success('已核验签名')
        setLeaseConfirmVisible(false)
        setCurrentLeaseForm(null)
        setLeaseSignatureList([])
        setLeaseSignatureDataUrl('')
        setLeaseNotes('')
        clearLeasePad()
        await fetchVehicles().catch(() => {})
        loadData()
      } else {
        message.error(res.error || '核验失败')
      }
    } catch (error) {
      console.error(error)
      message.error('核验失败')
    }
  }

  const handleCancelLeaseForm = (item: any) => {
    const isReturn = String(item?.type || '') === 'return'
    const isSigned = String(item?.status || '') === 'signed'
    const vehicleId =
      item?.vehicleId ||
      vehicles.find(v => String(v.plate || '').trim() === String(item?.plate || '').trim())?._id ||
      vehicles.find(v => String(v.plate || '').trim() === String(item?.plate || '').trim())?.id

    Modal.confirm({
      title: `确认撤销${isReturn ? '退租单' : '出租单'}？`,
      content: isSigned
        ? `将撤销 ${item?.plate || ''} 的已签${isReturn ? '退租单' : '出租单'}，并同步回滚车辆状态。此操作不可撤回。`
        : `将删除 ${item?.plate || ''} 的待签${isReturn ? '退租单' : '出租单'}。此操作不可撤回。`,
      okText: '确认撤销',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const res = await simpleDirectCloudApi.deleteLeaseForm(
          item?._id || item?.id,
          isReturn,
          vehicleId
        )
        if (res.success) {
          message.success('撤销成功')
          await fetchVehicles().catch(() => {})
          loadData()
        } else {
          message.error(res.error || '撤销失败')
        }
      }
    })
  }

  const handleRevokeReceipt = async (receipt: any) => {
    Modal.confirm({
      title: '确认撤销这笔已收款？',
      content: '撤销后会恢复为待收款，并清除出纳确认信息。',
      okText: '确认撤销',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const raw = receipt.rawReceipt || {}
        const items = Array.isArray(raw.items) && raw.items.length ? raw.items : [{ ...raw }]
        const resetItems = items.map((it: any) => ({
          ...it,
          status: 'submitted',
          verifiedAmount: null,
          receivedAmount: it.expectedAmount ?? it.amount ?? it.receivedAmount,
          verifiedAt: null,
          cashierId: null,
          cashierName: null,
          cashierSignatureUrl: null,
          cashierNotes: null
        }))
        const res = await simpleDirectCloudApi.updateReceipt(receipt.receiptId, {
          status: 'submitted',
          items: resetItems,
          verifiedAt: null,
          cashierId: null,
          cashierName: null,
          cashierSignatureUrl: null,
          cashierNotes: null
        })
        if (res.success) {
          message.success('已撤销收款')
          await fetchVehicles().catch(() => {})
          loadData()
        } else {
          message.error(res.error || '撤销失败')
        }
      }
    })
  }

  const handleDeleteReceipt = (receipt: any) => {
    const receiptId = receipt?.receiptId || receipt?._id || receipt?.id
    if (!receiptId) {
      message.error('凭证ID不存在，无法删除')
      return
    }
    Modal.confirm({
      title: '确认删除该凭证？',
      content: '删除后不可恢复，请确认是录错凭证。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const res = await simpleDirectCloudApi.deleteReceipt(String(receiptId))
        if (res.success) {
          // Backfill vehicle startDate from remaining receipt ranges after deleting a mistaken receipt.
          const normalizeKey = (val: any) => String(val ?? '').trim().toUpperCase()
          const matchVehicleByAnyId = (vehicleId: any, plate: any) => {
            const vid = normalizeKey(vehicleId)
            const vpl = normalizeKey(plate)
            return vehicles.find((v: any) => {
              const keys = [v._id, v.id, v.plate, v.forklift_no].map(normalizeKey)
              return (vid && keys.includes(vid)) || (vpl && keys.includes(vpl))
            })
          }

          const rawDeleted = receipt?.rawReceipt || receipt
          const deletedItems = getReceiptItems(rawDeleted)
          const affectedVehicleMap = new Map<string, any>()
          for (const it of (deletedItems.length ? deletedItems : [rawDeleted])) {
            const v = matchVehicleByAnyId(it?.vehicleId ?? rawDeleted?.vehicleId, it?.plate ?? rawDeleted?.plate)
            if (!v) continue
            const key = String(v._id || v.id || v.plate)
            affectedVehicleMap.set(key, v)
          }

          if (affectedVehicleMap.size > 0) {
            const allRes = await simpleDirectCloudApi.getReceipts()
            const allReceipts = allRes.success && allRes.data ? allRes.data : []
            const latestRangeEndMap = new Map<string, string>()

            for (const r of allReceipts) {
              const items = getReceiptItems(r)
              const sourceItems = items.length ? items : [r]
              for (const it of sourceItems) {
                if (!hasExplicitChargeRange(it, r)) continue
                const endRaw = String(it?.chargeRangeEnd || r?.chargeRangeEnd || '').trim()
                if (!endRaw || !dayjs(endRaw).isValid()) continue
                const v = matchVehicleByAnyId(it?.vehicleId ?? r?.vehicleId, it?.plate ?? r?.plate)
                if (!v) continue
                const key = String(v._id || v.id || v.plate)
                const prev = latestRangeEndMap.get(key)
                if (!prev || dayjs(endRaw).isAfter(dayjs(prev), 'day')) {
                  latestRangeEndMap.set(key, endRaw)
                }
              }
            }

            for (const [key, vehicle] of affectedVehicleMap.entries()) {
              const latestEnd = latestRangeEndMap.get(key)
              const pricingHistory = Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : []
              const pricingStarts = pricingHistory
                .map((p: any) => String(p?.startDate || '').trim())
                .filter((d: string) => d && dayjs(d).isValid())
                .sort((a: string, b: string) => dayjs(a).valueOf() - dayjs(b).valueOf())
              const fallbackStart = pricingStarts[0] || String(vehicle.startDate || '')
              const recalculatedStart = latestEnd || fallbackStart
              if (!recalculatedStart || !dayjs(recalculatedStart).isValid()) continue
              if (String(vehicle.startDate || '') === recalculatedStart) continue
              const docId = vehicle._id || vehicle.id || vehicle.plate
              await simpleDirectCloudApi.updateVehicle(docId, {
                _id: docId,
                startDate: recalculatedStart
              } as any)
            }
          }

          message.success('凭证已删除')
          await fetchVehicles().catch(() => {})
          loadData()
        } else {
          message.error(res.error || '删除失败')
        }
      }
    })
  }

  if (loading) {
      return (
        <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </div>
      )
    }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--warning)' }}>CASH</Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || 'cashier'}</Title>
                  <Text type="secondary">{user?.info || '收款员'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出登录</Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待确认" value={statistics.pendingVerification} valueStyle={{ color: 'var(--accent)' }} prefix={<AuditOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="今日确认" value={statistics.verifiedToday} valueStyle={{ color: 'var(--positive)' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待收金额" value={statistics.pendingAmount} valueStyle={{ color: 'var(--warning)' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="累计收款" value={statistics.totalProcessed} valueStyle={{ color: '#52c41a' }} prefix={<DollarOutlined />} formatter={(v) => `￥${Number(v || 0).toFixed(2)}`} />
          </Card>
        </Col>
      </Row>

      <Card
        style={{
          marginBottom: 24,
          borderColor: (pendingReturnForms.length || maintenanceWithoutSignedReturnDoc.length) ? 'var(--warning)' : 'var(--line)',
          background: (pendingReturnForms.length || maintenanceWithoutSignedReturnDoc.length) ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space wrap>
              <Tag color={pendingReturnForms.length ? 'orange' : 'default'}>
                待核验退租单 {pendingReturnForms.length}
              </Tag>
              <Tag color={maintenanceWithoutSignedReturnDoc.length ? 'red' : 'default'}>
                退租状态缺单据 {maintenanceWithoutSignedReturnDoc.length}
              </Tag>
              <Text type="secondary">
                用于核对“车辆已退租”是否有已签单据，防止绕过流程。
              </Text>
            </Space>
          </Col>
          <Col>
            <Button onClick={() => setActiveTab('lease')}>
              去核验退租/出租单
            </Button>
          </Col>
        </Row>
      </Card>

      {pendingConfirmReceipts.length > 0 && (
        <Card
          style={{
            marginBottom: 24,
            borderColor: 'var(--warning)',
            background: 'rgba(192, 122, 31, 0.06)'
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space wrap>
                <Tag color="orange">待确认收款 {pendingConfirmReceipts.length}</Tag>
                <Text type="secondary">
                  这些单据已经录入了实际收入，但还没有完成出纳确认，未计入“已收入”。
                </Text>
              </Space>
            </Col>
            <Col>
              <Button type="primary" onClick={() => {
                const first = pendingConfirmReceipts[0]
                if (first) handleConfirmReceipt(first)
              }}>
                去确认首张收款
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'pending',
              label: (
                <Badge count={pendingReceipts.length} size="small">
                  <span>待收款</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={pendingReceipts}
                  renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button key="preview" type="primary" onClick={() => openReceiptPreview(item, 'pending')}>
                            查看凭证
                          </Button>,
                          <Button key="confirm" onClick={() => handleConfirmReceipt(item)}>
                            确认收款
                          </Button>,
                          <Button key="delete" danger onClick={() => handleDeleteReceipt(item)}>
                            删除凭证
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate}</Avatar>}
                          title={
                            <Space>
                              <Text strong>{item.plate}</Text>
                              <Tag color={needsCashierConfirm(item) ? 'gold' : 'orange'}>
                                {needsCashierConfirm(item) ? '待确认' : '待收款'}
                              </Tag>
                              {isDepositOnlyReceipt(item) && <Tag color="purple">仅押金</Tag>}
                            </Space>
                          }
                          description={
                            <div>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">车型</Text> <Text>{item.model}</Text></Col>
                                <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">实际收入金额</Text> <Text strong style={{ color: 'var(--positive)' }}>￥{formatMoney(getActualIncomeAmount(item))}</Text></Col>
                                <Col span={12}><Text type="secondary">到期日</Text> <Text>{item.reminderDue || item.dueDate || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">提醒周期</Text> <Tag>{getPeriodLabel(item.reminderPeriod, item.reminderPeriodMonths)}</Tag></Col>
                                <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{Number(item.reminderAmount ?? item.amount ?? 0).toFixed(2)}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              </Row>
                              {getRangeLabel(item) && (
                                <Row gutter={16}>
                                  <Col span={24}><Text type="secondary">收款区间</Text> <Text>{getRangeLabel(item)}</Text></Col>
                                </Row>
                              )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                />
              )
            },
            {
              key: 'lease',
              label: (
                <Badge count={leaseForms.length} size="small">
                  <span>退租/出租单</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={leaseForms}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="view" onClick={() => openLeasePreview(item)}>
                          查看单据
                        </Button>,
                        <Button key="cancel" danger onClick={() => handleCancelLeaseForm(item)}>
                          撤销
                        </Button>,
                        <Button key="sign" type="primary" onClick={() => handleConfirmLease(item)}>
                          核验签名
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--accent)' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color={item.type === 'return' ? 'volcano' : 'blue'}>
                              {item.type === 'return' ? '退租单' : '出租单'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">{item.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{item.unitName || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">提交人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">{item.type === 'return' ? '退租时间' : '提交时间'}</Text> <Text>{(item.type === 'return' ? item.returnTime : item.createdAt) ? dayjs(item.type === 'return' ? item.returnTime : item.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                            {item.notes ? (
                              <Row gutter={16}>
                                <Col span={24}><Text type="secondary">备注</Text> <Text>{item.notes}</Text></Col>
                              </Row>
                            ) : null}
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'lease-signed',
              label: (
                <Badge count={signedLeaseForms.length} size="small">
                  <span>已签退租/出租单</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={signedLeaseForms}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button key="view" onClick={() => openLeasePreview(item)}>
                          查看单据
                        </Button>,
                        <Button key="cancel" danger onClick={() => handleCancelLeaseForm(item)}>
                          撤销
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color="green">已签名</Tag>
                            <Tag color={item.type === 'return' ? 'volcano' : 'blue'}>
                              {item.type === 'return' ? '退租单' : '出租单'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">{item.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{item.unitName || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">签名人</Text> <Text>{item.cashierName || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">签名时间</Text> <Text>{item.signedAt ? dayjs(item.signedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
            {
              key: 'verified',
              label: (
                <Badge count={verifiedReceipts.length} size="small">
                  <span>已收款</span>
                </Badge>
              ),
              children: (
                <>
                  <Input.Search
                    placeholder="搜索已收款(车牌/公司/制单人/备注/单号)"
                    allowClear
                    value={verifiedSearchKeyword}
                    onChange={(e) => setVerifiedSearchKeyword(e.target.value)}
                    style={{ marginBottom: 12, maxWidth: 420 }}
                  />
                  <List
                    itemLayout="vertical"
                    dataSource={filteredVerifiedReceipts}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button key="receipt" onClick={() => openReceiptPreview(item, 'verified')}>
                            查看收据
                          </Button>,
                          <Button key="revoke" danger onClick={() => handleRevokeReceipt(item)}>
                            撤销
                          </Button>,
                          <Button key="delete" danger onClick={() => handleDeleteReceipt(item)}>
                            删除凭证
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: 'var(--positive)' }}>{item.plate}</Avatar>}
                          title={
                            <Space>
                              <Text strong>{item.plate}</Text>
                              <Tag color="green">已收款</Tag>
                              {isDepositOnlyReceipt(item) && <Tag color="purple">仅押金</Tag>}
                            </Space>
                          }
                          description={
                            <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company}</Text></Col>
                              <Col span={12}><Text type="secondary">周期</Text> <Tag>{item.periodKey || '周期'}</Tag></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                            </Row>
                            <Text type="secondary">实际收入金额</Text> <Text strong>￥{formatMoney(getActualIncomeAmount(item))}</Text><br />
                            <Text type="secondary">确认时间</Text> <Text>{item.verifiedAt ? dayjs(item.verifiedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                            {getRangeLabel(item) && (
                              <>
                                <br />
                                <Text type="secondary">扣款范围</Text> <Text>{getRangeLabel(item)}</Text>
                              </>
                            )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      <Card
        style={{
          marginBottom: 24,
          borderColor: expiringVehiclesAll.length ? 'var(--warning)' : 'var(--line)',
          background: expiringVehiclesAll.length ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--warning)' }} />
              <Text strong>到期提醒</Text>
              <Tag color={expiringVehiclesAll.length ? 'orange' : 'default'}>
                {expiringVehiclesAll.length} 台
              </Tag>
              {expiringVehiclesAll.length ? (
                <Text type="secondary">请优先核对即将到期的车辆</Text>
              ) : (
                <Text type="secondary">当前没有需要提醒的车辆</Text>
              )}
            </Space>
          </Col>
          <Col>
            <Button type="primary" onClick={() => setExpiringModalVisible(true)} disabled={!expiringVehiclesAll.length}>
              查看全部
            </Button>
          </Col>
        </Row>
        {expiringVehiclesAll.length > 0 && (
          <List
            itemLayout="vertical"
            dataSource={expiringTop}
            renderItem={(vehicle) => {
              const dueDate = getVehicleDueDate(vehicle)
              return (
                <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                    title={
                      <Space>
                        <Text strong>{vehicle.plate}</Text>
                        <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                        <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">公司：</Text>
                        <Text>{vehicle.company || '-'}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                        <Text>{vehicle.contactPerson || '-'}</Text>
                      </div>
                    }
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Card>

      <Modal
        title="确认收款"
        open={confirmVisible}
        onOk={handleSubmitConfirm}
        onCancel={() => {
          if (submittingConfirm) return
          setConfirmVisible(false); setCurrentReceipt(null); setCashierNotes(''); setPaymentMethod(paymentMethodOptions[0])
        }}
        okText="确认收款"
        confirmLoading={submittingConfirm}
        okButtonProps={{ disabled: submittingConfirm }}
        cancelButtonProps={{ disabled: submittingConfirm }}
      >
        {currentReceipt && (
          <div>
            <p>车牌:{currentReceipt.plate}</p>
            <p>实际收入金额:￥{formatMoney(getActualIncomeAmount(currentReceipt))}</p>
            {getRangeLabel(currentReceipt) && (
              <p>收款区间:{getRangeLabel(currentReceipt)}</p>
            )}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">收款方式</Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                value={paymentMethod}
                options={paymentMethodOptions.map((v) => ({ label: v, value: v }))}
                onChange={setPaymentMethod}
              />
            </div>
            <Tag color="blue">确认收款时将自动生成出纳签名</Tag>
            <div style={{ marginTop: 10, marginBottom: 10, color: '#8c6d1f', lineHeight: 1.7 }}>
              说明：若本单属于预收租金、仅押金、或未来账期收款，确认后待收可能不会立刻下降，这种情况是正常的。
            </div>
            <Input.TextArea
              rows={3}
              placeholder="备注。若系统提示待收未下降，这里必须填写放行原因"
              value={cashierNotes}
              onChange={(e) => setCashierNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
      <Modal
        title="核验退租/出租单"
        open={leaseConfirmVisible}
        onOk={handleSubmitLeaseConfirm}
        onCancel={() => { setLeaseConfirmVisible(false); setCurrentLeaseForm(null); setLeaseSignatureList([]); setLeaseSignatureDataUrl(''); setLeaseNotes(''); clearLeasePad() }}
        okText="确认签名"
      >
        {currentLeaseForm && (
          <div>
            <p>车牌:{currentLeaseForm.plate || '-'}</p>
            <p>{currentLeaseForm.type === 'relet' ? '出租单位' : '退租单位'}:{currentLeaseForm.unitName || '-'}</p>
            <div style={{ marginBottom: 8 }}>
              <div style={{ border: '1px solid #ddd', borderRadius: 4, width: '100%', height: 180, marginBottom: 8 }}>
                <canvas
                  ref={leaseCanvasRef}
                  width={1}
                  height={CANVAS_HEIGHT}
                  style={{ width: '100%', height: CANVAS_HEIGHT, touchAction: 'none', display: 'block' }}
                  onPointerDown={startLeaseDraw}
                  onPointerMove={drawLease}
                  onPointerUp={endLeaseDraw}
                  onPointerLeave={endLeaseDraw}
                  onPointerCancel={endLeaseDraw}
                />
              </div>
              <Space style={{ marginBottom: 8 }}>
                <Button onClick={clearLeasePad}>清空签名</Button>
                {leaseSignatureDataUrl && <Tag color="green">已签名</Tag>}
              </Space>
            </div>
            <Upload
              listType="picture-card"
              fileList={leaseSignatureList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setLeaseSignatureList(fileList)}
              maxCount={1}
            >
              {leaseSignatureList.length >= 1 ? null : '上传签名/附件'}
            </Upload>
            <Input.TextArea
              rows={3}
              placeholder="备注(可选)"
              value={leaseNotes}
              onChange={(e) => setLeaseNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
      <Modal
        title={receiptPreviewMode === 'pending'
          ? (isDepositOnlyReceipt(receiptPreview) ? '押金凭证' : '收款凭证')
          : (isDepositOnlyReceipt(receiptPreview) ? '押金收据' : '收据')}
        open={receiptPreviewVisible}
        onCancel={() => { setReceiptPreviewVisible(false); setReceiptPreview(null); setReceiptSignatureUrl(''); setReceiptMakerSealUrl('') }}
        footer={
          receiptPreviewMode === 'pending'
            ? [
                <Button key="cancel" onClick={() => { setReceiptPreviewVisible(false); setReceiptPreview(null) }}>
                  取消
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  loading={submittingConfirm}
                  disabled={submittingConfirm}
                  onClick={() => {
                    if (receiptPreview) {
                      setReceiptPreviewVisible(false)
                      handleConfirmReceipt(receiptPreview)
                    }
                  }}
                >
                  确认收款
                </Button>
              ]
            : null
        }
        width="92vw"
        style={{ maxWidth: 1280 }}
        styles={{ body: { maxHeight: '84vh', overflow: 'auto' } }}
      >
        {receiptPreview && (
          (() => {
            const rawReceipt = receiptPreview.rawReceipt || receiptPreview
            const rawItems = getReceiptItems(rawReceipt)
            const sourceItems = rawItems.length ? rawItems : [rawReceipt]
            const byVehicle = new Map<string, any>()
            sourceItems.forEach((it: any) => {
              const plate = it.plate || receiptPreview.plate || '-'
              const vehicleId = String(it.vehicleId || it._id || it.id || plate)
              const explicitRange = hasExplicitChargeRange(it, rawReceipt)
              const chargeRangeStart = explicitRange ? (it.chargeRangeStart || rawReceipt.chargeRangeStart) : undefined
              const chargeRangeEnd = explicitRange ? (it.chargeRangeEnd || rawReceipt.chargeRangeEnd) : undefined
              const expected = Number(it.expectedAmount ?? it.amount ?? 0) || 0
              const basis = Number(it.actualAmount ?? it.actualAmountDraft ?? it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0) || 0
              const prev = byVehicle.get(vehicleId)
              if (prev) {
                prev.expected += expected
                prev.basis += basis
                if (chargeRangeStart && (!prev.chargeRangeStart || dayjs(chargeRangeStart).isBefore(dayjs(prev.chargeRangeStart), 'day'))) {
                  prev.chargeRangeStart = chargeRangeStart
                }
                if (chargeRangeEnd && (!prev.chargeRangeEnd || dayjs(chargeRangeEnd).isAfter(dayjs(prev.chargeRangeEnd), 'day'))) {
                  prev.chargeRangeEnd = chargeRangeEnd
                }
              } else {
                byVehicle.set(vehicleId, {
                  ...it,
                  plate,
                  expected,
                  basis,
                  chargeRangeStart,
                  chargeRangeEnd
                })
              }
            })
            const items = Array.from(byVehicle.values())
            const sourceDepositTotal = sourceItems.reduce((sum: number, it: any) => sum + (Number(it.depositAmount || 0) || 0), 0)
            const depositTotal = Number(
              receiptPreview.depositAmount ??
              receiptPreview.rawReceipt?.depositAmount ??
              sourceDepositTotal ??
              items.reduce((sum: number, it: any) => sum + (Number(it.depositAmount || 0) || 0), 0) ??
              0
            ) || 0
            const actualAmountValue = Number(
              receiptPreview.actualAmount ??
              receiptPreview.actualAmountDraft ??
              receiptPreview.rawReceipt?.actualAmountDraft ??
              receiptPreview.rawReceipt?.actualAmount ??
              sourceItems.reduce((s: number, it: any) => s + (Number(it.actualAmount ?? it.actualAmountDraft ?? it.receivedAmount ?? it.verifiedAmount ?? it.amount ?? 0) || 0), 0)
            ) || 0
            const rentActualTotal = Math.max(actualAmountValue - depositTotal, 0)
            const basisSum = items.reduce((sum: number, it: any) => sum + Number(it.basis || it.expected || 0), 0)
            const itemsWithActual = items.map((it: any, idx: number) => {
              const basis = Number(it.basis || it.expected || 0)
              let actual = 0
              if (items.length === 1) {
                actual = rentActualTotal
              } else if (basisSum > 0) {
                actual = rentActualTotal * (basis / basisSum)
              } else {
                actual = rentActualTotal / items.length
              }
              if (idx === items.length - 1) {
                const prevAllocated = items.slice(0, idx).reduce((s: number, p: any) => {
                  const b = Number(p.basis || p.expected || 0)
                  if (items.length === 1) return s
                  if (basisSum > 0) return s + (rentActualTotal * (b / basisSum))
                  return s + (rentActualTotal / items.length)
                }, 0)
                actual = Math.max(rentActualTotal - prevAllocated, 0)
              }
              return { ...it, actualAmount: actual }
            })
            const total = itemsWithActual.reduce((sum: number, it: any) => sum + Number(it.actualAmount || 0), 0)
            const receiptNo = receiptPreview.receiptId || receiptPreview._id || receiptPreview.id || '-'
            const date = formatDate(receiptPreview.paymentDate || receiptPreview.verifiedAt || receiptPreview.createdAt)
            const rangeLabel = getRangeLabel(receiptPreview)
            const cashierNoteValue = receiptPreview.cashierNotes ||
              receiptPreview.rawReceipt?.cashierNotes ||
              receiptPreview.notes ||
              receiptPreview.rawReceipt?.notes ||
              ''
            const paymentMethodValue =
              receiptPreview.paymentMethod ||
              receiptPreview.rawReceipt?.paymentMethod ||
              items[0]?.paymentMethod
            const tradeInAmount = Number(receiptPreview.tradeInAmount ?? receiptPreview.rawReceipt?.tradeInAmount ?? 0) || 0
            const depositOffsetAmount = Number(receiptPreview.depositOffsetAmount ?? receiptPreview.rawReceipt?.depositOffsetAmount ?? 0) || 0
            const otherIncomeAmount = Number(receiptPreview.otherIncomeAmount ?? receiptPreview.rawReceipt?.otherIncomeAmount ?? 0) || 0
            const accountingScenario = String(receiptPreview.accountingScenario || receiptPreview.rawReceipt?.accountingScenario || 'auto')
            const company = receiptPreview.company || receiptPreview.customerName || '-'
            const makerName =
              receiptPreview.createdBy ||
              receiptPreview.rawReceipt?.createdBy ||
              '-'
            const makerSeal = receiptMakerSealUrl
            const receiptTitle = isDepositOnlyReceipt(receiptPreview) ? '押金收据' : '收款收据'
            const receiptGridColumns = '78px 1.6fr 1.65fr 60px 60px 110px 90px 110px'
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>制单人：{makerName}</div>
                  <Button onClick={handleExportReceipt}>导出/打印</Button>
                </div>
                <div className="receipt-preview-scroll">
                  <div
                    ref={receiptPrintRef}
                    className="receipt-preview-sheet"
                    style={{
                      fontFamily: '"SimSun","STSong","Noto Serif SC",serif',
                      color: '#222'
                    }}
                  >
                    <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 8 }}>
                      {receiptTitle}
                    </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                    <div>客户名称：{company}</div>
                    <div>No：{receiptNo}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    <div>收款范围：{rangeLabel || '-'}</div>
                    <div>日期：{date}</div>
                  </div>
                  <div style={{ border: '1px solid #333', marginTop: 10 }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: receiptGridColumns,
                      fontWeight: 700,
                      borderBottom: '1px solid #333'
                    }}>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>货号</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333' }}>品名及规格</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333' }}>收款区间</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>单位</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>数量</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>区间应收</div>
                      <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>单价</div>
                      <div style={{ padding: '8px 10px', textAlign: 'right' }}>金额</div>
                    </div>
                    {itemsWithActual.map((it: any, idx: number) => {
                      const expected = Number(it.expected ?? it.expectedAmount ?? it.amount ?? 0)
                      const amount = Number(it.actualAmount ?? 0)
                      const itemRange = getRangeLabel(it)
                      return (
                        <div
                          key={idx}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: receiptGridColumns,
                            borderBottom: idx === itemsWithActual.length - 1 ? 'none' : '1px solid #333'
                          }}
                        >
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }} />
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', lineHeight: 1.6 }}>
                            车辆租金（{it.plate || receiptPreview.plate || '-'}）
                          </div>
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', lineHeight: 1.6 }}>{itemRange || '-'}</div>
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>台</div>
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>1</div>
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>
                            {formatMoney(expected)}
                          </div>
                          <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>
                            {formatMoney(expected)}
                          </div>
                          <div style={{ padding: '8px 10px', textAlign: 'right' }}>{formatMoney(amount)}</div>
                        </div>
                      )
                    })}
                    {depositTotal > 0 && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: receiptGridColumns,
                          borderTop: itemsWithActual.length ? '1px solid #333' : 'none'
                        }}
                      >
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }} />
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', lineHeight: 1.6 }}>
                          押金（{receiptPreview.plate || '-' }）
                        </div>
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>-</div>
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>笔</div>
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'center' }}>1</div>
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>
                          -
                        </div>
                        <div style={{ padding: '8px 10px', borderRight: '1px solid #333', textAlign: 'right' }}>
                          {formatMoney(depositTotal)}
                        </div>
                        <div style={{ padding: '8px 10px', textAlign: 'right' }}>{formatMoney(depositTotal)}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                    <div>合计人民币：{formatMoney(total)}</div>
                    <div>￥{formatMoney(total)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>押金：{formatMoney(depositTotal)}</div>
                    <div>￥{formatMoney(depositTotal)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>实际收入：{actualAmountValue != null ? formatMoney(actualAmountValue) : '-'}</div>
                    <div>￥{actualAmountValue != null ? formatMoney(actualAmountValue) : '-'}</div>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    收款方式：{paymentMethodValue || '-'}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    做账场景：{getAccountingScenarioLabel(accountingScenario)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>旧车折抵：{formatMoney(tradeInAmount)}</div>
                    <div>￥{formatMoney(tradeInAmount)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>押金抵扣：{formatMoney(depositOffsetAmount)}</div>
                    <div>￥{formatMoney(depositOffsetAmount)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <div>其他收入：{formatMoney(otherIncomeAmount)}</div>
                    <div>￥{formatMoney(otherIncomeAmount)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                    <div>制单人：{makerName}</div>
                    <div>会计：</div>
                    <div>出纳：{receiptPreview.cashierName || user?.nickName || '-'}</div>
                    <div>出纳单位：</div>
                  </div>
                  <div style={{ marginTop: 8 }}>备注：{cashierNoteValue || '-'}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 12 }}>
                    <div>
                      <div style={{ textAlign: 'center', marginBottom: 4 }}>制单人签名</div>
                      {makerSeal ? (
                        <img
                          src={makerSeal}
                          alt="maker seal"
                          style={{ width: 220, height: 120, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
                        />
                      ) : (
                        <div style={{ width: 220, height: 120, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                          未上传制单人签名
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ textAlign: 'center', marginBottom: 4 }}>出纳员签名</div>
                      {receiptSignatureUrl ? (
                        <img
                          src={receiptSignatureUrl}
                          alt="cashier signature"
                          style={{ width: 220, height: 120, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
                        />
                      ) : (
                        <div style={{ width: 220, height: 120, border: '1px dashed #ccc' }} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )
          })()
        )}
      </Modal>
      <Modal
        title={leasePreview?.type === 'return' ? '退租单' : '出租单'}
        open={leasePreviewVisible}
        onCancel={() => { setLeasePreviewVisible(false); setLeasePreview(null); setLeaseSignatureUrl(''); setLeaseMakerSealUrl('') }}
        footer={null}
        width={720}
      >
        {leasePreview && (
          <div>
            <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 8 }}>
              {leasePreview.type === 'return' ? '退租单' : '出租单'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>{leasePreview.type === 'relet' ? '出租单位' : '退租单位'}：{leasePreview.unitName || '-'}</div>
              <div>No：{leasePreview._id || leasePreview.id || '-'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <div>车牌：{leasePreview.plate || '-'}</div>
              <div>{leasePreview.type === 'return' ? '退租时间' : '日期'}：{(leasePreview.type === 'return' ? leasePreview.returnTime : leasePreview.createdAt) ? dayjs(leasePreview.type === 'return' ? leasePreview.returnTime : leasePreview.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
            </div>
            <div style={{ border: '1px solid #333', marginTop: 10 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>{leasePreview.type === 'relet' ? '出租单位' : '退租单位'}</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单位</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>数量</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单价</div>
                <div style={{ padding: '6px 8px' }}>金额</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const plate = leasePreview.plate || '-'
                    const model = leasePreview.model ? ` - ${leasePreview.model}` : ''
                    return `车辆租赁（${plate}）${model}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>{leasePreview.unitName || '-'}</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>台</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>1</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333', textAlign: 'right' }}>-</div>
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>配置：{leasePreview.configuration || '-'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <div>备注：{leasePreview.notes || '-'}</div>
              <div>状态：{leasePreview.status === 'signed' ? '已签名' : '待签名'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>制单人：{leasePreview.createdBy || '-'}</div>
              <div>会计：</div>
              <div>出纳：{leasePreview.cashierName || '-'}</div>
              <div>出纳单位：</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 12 }}>
              <div>
                <div style={{ textAlign: 'center', marginBottom: 4 }}>制单人签名</div>
                {leaseMakerSealUrl ? (
                  <img
                    src={leaseMakerSealUrl}
                    alt="maker seal"
                    style={{ width: 220, height: 120, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ width: 220, height: 120, border: '1px dashed #ccc' }} />
                )}
              </div>
              <div>
                <div style={{ textAlign: 'center', marginBottom: 4 }}>出纳员签名</div>
                {leaseSignatureUrl ? (
                  <img
                    src={leaseSignatureUrl}
                    alt="cashier signature"
                    style={{ width: 220, height: 120, border: '1px solid #ddd', borderRadius: 4, objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ width: 220, height: 120, border: '1px dashed #ccc' }} />
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="到期提醒"
        open={expiringModalVisible}
        onCancel={() => setExpiringModalVisible(false)}
        footer={null}
        width={720}
      >
        <List
          itemLayout="vertical"
          dataSource={expiringVehiclesAll}
          renderItem={(vehicle) => {
            const dueDate = getVehicleDueDate(vehicle)
            return (
              <List.Item key={vehicle._id || vehicle.id || vehicle.plate}>
                <List.Item.Meta
                  avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                  title={
                    <Space>
                      <Text strong>{vehicle.plate}</Text>
                      <Tag color="orange">{dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                      <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary">公司：</Text>
                      <Text>{vehicle.company || '-'}</Text>
                      <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                      <Text>{vehicle.contactPerson || '-'}</Text>
                    </div>
                  }
                />
              </List.Item>
            )
          }}
        />
      </Modal>
    </div>
  )
}

export default CashierPage
